/**
 * Currency Formatting Utilities
 * 
 * Provides currency formatting functions using Intl.NumberFormat
 * Supports EUR (Europe) and BRL (Brazil)
 * 
 * This utility replaces the formatPrice/formatCurrency from LocaleContext
 * to reduce dependencies and improve maintainability.
 */

// Default configuration based on stored restaurant settings
export const getDefaultCurrency = () => {
  try {
    const restaurantData = localStorage.getItem('kaiso_selected_restaurant');
    if (restaurantData) {
      const restaurant = JSON.parse(restaurantData);
      // Check if restaurant is in Brazil
      if (restaurant.country === 'BR' || restaurant.locale === 'pt-BR') {
        return { locale: 'pt-BR', currency: 'BRL' };
      }
    }
  } catch (e) {
    // Ignore errors, use default
  }
  return { locale: 'es-ES', currency: 'EUR' };
};

/**
 * Format a number as currency
 * 
 * @param {number} amount - The amount to format
 * @param {string} locale - Locale string (e.g., 'es-ES', 'pt-BR')
 * @param {string} currency - Currency code (e.g., 'EUR', 'BRL')
 * @returns {string} Formatted currency string
 * 
 * @example
 * formatPrice(10.50) // "10,50 €" or "R$ 10,50"
 * formatPrice(10.50, 'es-ES', 'EUR') // "10,50 €"
 * formatPrice(10.50, 'pt-BR', 'BRL') // "R$ 10,50"
 */
export function formatPrice(amount, locale, currency) {
  const defaults = getDefaultCurrency();
  const finalLocale = locale || defaults.locale;
  const finalCurrency = currency || defaults.currency;
  
  const num = Number(amount || 0);
  
  try {
    return new Intl.NumberFormat(finalLocale, {
      style: 'currency',
      currency: finalCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch (e) {
    // Fallback for invalid locale/currency
    return `${num.toFixed(2)} €`;
  }
}

/**
 * Format a number as currency with more spacing
 * Same as formatPrice but kept for API compatibility
 * 
 * @param {number} amount - The amount to format
 * @param {string} locale - Locale string
 * @param {string} currency - Currency code
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, locale, currency) {
  return formatPrice(amount, locale, currency);
}

/**
 * Get currency symbol for a given currency code
 * 
 * @param {string} currency - Currency code (e.g., 'EUR', 'BRL')
 * @returns {string} Currency symbol
 */
export function getCurrencySymbol(currency = 'EUR') {
  const symbols = {
    EUR: '€',
    BRL: 'R$',
    USD: '$',
    GBP: '£',
  };
  return symbols[currency] || currency;
}

/**
 * Parse a formatted currency string back to number
 * 
 * @param {string} formattedValue - Formatted currency string
 * @returns {number} Numeric value
 */
export function parseCurrency(formattedValue) {
  if (typeof formattedValue === 'number') return formattedValue;
  if (!formattedValue) return 0;
  
  // Remove currency symbols and spaces
  const cleaned = formattedValue
    .replace(/[€$R£]/g, '')
    .replace(/\s/g, '')
    .replace(/\./g, '') // Remove thousand separators (pt-BR uses .)
    .replace(',', '.'); // Convert decimal separator
  
  return parseFloat(cleaned) || 0;
}

// Export default configuration helper
export const currencyConfig = {
  EUR: { locale: 'es-ES', symbol: '€', position: 'after' },
  BRL: { locale: 'pt-BR', symbol: 'R$', position: 'before' },
};
